// Typed portal API client.
//
// Cross-origin contract: the SPA is served from ideation.vaneloop.com and calls
// the API at api.vaneloop.com. Every request therefore sends
// `credentials: 'include'` so the SameSite=None portal session cookie is
// attached (US2 AC2.3, paired with the US5 cookie fix).
//
// 401 interceptor: a single hook surface is exposed here. US6 wires the
// reactive session-expiry behaviour onto it. The unauthenticated auth endpoints
// (request-link, verify) are EXCLUDED from the interceptor so an invalid-link
// 401 cannot trigger an expiry redirect loop.

import { apiBaseUrl } from '../env';
import type {
  GenericMessageResponse,
  IdeaCreateRequest,
  IdeaListResponse,
  IdeaResponse,
  PortalCostCreateRequest,
  PortalCostDriversResponse,
  PortalCostResponse,
  PortalCostsResponse,
  PortalCostUpdateRequest,
  PortalDeleteResponse,
  PortalDomainsResponse,
  PortalSavingsCategoriesResponse,
  PortalSavingsCreateRequest,
  PortalSavingsListResponse,
  PortalSavingsResponse,
  PortalSavingsUpdateRequest,
  PortalTenantResponse,
  PortalToolsResponse,
  SessionResponse,
  TierResponse,
} from './types';

export class ApiError extends Error {
  status: number;
  /** Parsed `detail` payload from the API, when present. */
  detail: unknown;
  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

// ── 401 interceptor surface (behaviour wired in US6) ──
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

/** Register a handler invoked on a 401 from a PROTECTED endpoint. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

// Auth endpoints are unauthenticated. A 401 from these means "bad link", NOT
// "session expired" — they must never trigger the expiry redirect (US6 AC6.2).
const AUTH_PATHS = ['/api/portal/auth/request-link', '/api/portal/auth/verify'];

function isAuthPath(path: string): boolean {
  return AUTH_PATHS.some((p) => path === p || path.startsWith(p));
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body } = opts;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method,
    headers,
    credentials: 'include', // REQUIRED: cross-site session cookie delivery.
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !isAuthPath(path)) {
    // Protected endpoint rejected the session — fire the interceptor (US6).
    onUnauthorized?.();
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const detail =
      payload && typeof payload === 'object' && 'detail' in payload
        ? (payload as { detail: unknown }).detail
        : payload;
    const message =
      typeof detail === 'string' ? detail : `Request failed (${res.status})`;
    throw new ApiError(res.status, message, detail);
  }

  return payload as T;
}

export const portalApi = {
  // Auth (unauthenticated)
  requestLink(email: string): Promise<GenericMessageResponse> {
    return request('/api/portal/auth/request-link', {
      method: 'POST',
      body: { email },
    });
  },
  verify(token: string): Promise<GenericMessageResponse> {
    return request('/api/portal/auth/verify', { method: 'POST', body: { token } });
  },

  // Ideas (portal session)
  listIdeas(): Promise<IdeaListResponse> {
    return request('/api/portal/ideas/');
  },
  getIdea(id: string): Promise<IdeaResponse> {
    return request(`/api/portal/ideas/${id}`);
  },
  createIdea(body: IdeaCreateRequest): Promise<IdeaResponse> {
    return request('/api/portal/ideas/', { method: 'POST', body });
  },
  updateIdea(id: string, body: IdeaCreateRequest): Promise<IdeaResponse> {
    return request(`/api/portal/ideas/${id}`, { method: 'PUT', body });
  },
  /** Save draft with draft=true (default on create/update). */
  saveDraft(id: string | null, body: IdeaCreateRequest): Promise<IdeaResponse> {
    const payload = { ...body, draft: true };
    return id
      ? request(`/api/portal/ideas/${id}`, { method: 'PUT', body: payload })
      : request('/api/portal/ideas/', { method: 'POST', body: payload });
  },
  /** Validate and submit — promotes PORTAL_DRAFT → IDEA. */
  submitIdea(id: string | null, body: IdeaCreateRequest): Promise<IdeaResponse> {
    const payload = { ...body, draft: false };
    return id
      ? request(`/api/portal/ideas/${id}`, { method: 'PUT', body: payload })
      : request('/api/portal/ideas/', { method: 'POST', body: payload });
  },

  // Session info (portal session)
  me(): Promise<SessionResponse> {
    return request('/api/portal/me');
  },

  // Tier (portal session)
  getTier(): Promise<TierResponse> {
    return request('/api/portal/tier');
  },


  // Tools (portal session) — autocomplete catalogue
  listTools(): Promise<PortalToolsResponse> {
    return request('/api/portal/tools');
  },

  // Domains (portal session) — tenant domain catalogue
  listDomains(): Promise<PortalDomainsResponse> {
    return request('/api/portal/domains');
  },

  // ── Business-case cost line items (portal session) ──
  listCosts(ideaId: string): Promise<PortalCostsResponse> {
    return request(`/api/portal/ideas/${ideaId}/costs`);
  },
  createCost(
    ideaId: string,
    body: PortalCostCreateRequest,
  ): Promise<PortalCostResponse> {
    return request(`/api/portal/ideas/${ideaId}/costs`, { method: 'POST', body });
  },
  updateCost(
    ideaId: string,
    costId: string,
    body: PortalCostUpdateRequest,
  ): Promise<PortalCostResponse> {
    return request(`/api/portal/ideas/${ideaId}/costs/${costId}`, {
      method: 'PUT',
      body,
    });
  },
  deleteCost(ideaId: string, costId: string): Promise<PortalDeleteResponse> {
    return request(`/api/portal/ideas/${ideaId}/costs/${costId}`, {
      method: 'DELETE',
    });
  },

  // ── Business-case savings line items (portal session) ──
  listSavings(ideaId: string): Promise<PortalSavingsListResponse> {
    return request(`/api/portal/ideas/${ideaId}/savings`);
  },
  createSavings(
    ideaId: string,
    body: PortalSavingsCreateRequest,
  ): Promise<PortalSavingsResponse> {
    return request(`/api/portal/ideas/${ideaId}/savings`, { method: 'POST', body });
  },
  updateSavings(
    ideaId: string,
    savingsId: string,
    body: PortalSavingsUpdateRequest,
  ): Promise<PortalSavingsResponse> {
    return request(`/api/portal/ideas/${ideaId}/savings/${savingsId}`, {
      method: 'PUT',
      body,
    });
  },
  deleteSavings(
    ideaId: string,
    savingsId: string,
  ): Promise<PortalDeleteResponse> {
    return request(`/api/portal/ideas/${ideaId}/savings/${savingsId}`, {
      method: 'DELETE',
    });
  },

  // ── Catalogues + tenant (portal session) ──
  listCostDrivers(): Promise<PortalCostDriversResponse> {
    return request('/api/portal/cost-drivers');
  },
  listSavingsCategories(): Promise<PortalSavingsCategoriesResponse> {
    return request('/api/portal/savings/categories');
  },
  getTenant(): Promise<PortalTenantResponse> {
    return request('/api/portal/tenant');
  },
};

// Exposed for unit tests of the interceptor/exclusion logic.
export const __test = { isAuthPath };
